/**
 * Primera pantalla al entrar (propio o por invitación): elegir el nombre visible
 * en partida (≤ 30 chars). Reutiliza el lenguaje visual del lobby.
 */
import { MAX_NAME, sanitizeName, checkName } from "./identity";
import { injectThemeTokens } from "../theme";

const CSS = `
.nm-root{position:fixed;inset:0;z-index:22;display:none;align-items:center;justify-content:center;
  padding:var(--sp-4);font-family:var(--tb-font-ui);color:var(--tb-text);
  background:radial-gradient(130% 110% at 50% -10%,rgba(16,26,38,0.96),rgba(4,6,10,0.99));}
.nm-root.open{display:flex;}
.nm-root::before{content:"";position:absolute;inset:0;pointer-events:none;opacity:.6;
  background-image:linear-gradient(rgba(34,230,255,0.05) 1px,transparent 1px),
    linear-gradient(90deg,rgba(34,230,255,0.05) 1px,transparent 1px);background-size:4rem 4rem;
  -webkit-mask-image:radial-gradient(120% 85% at 50% 0%,#000,transparent 78%);
  mask-image:radial-gradient(120% 85% at 50% 0%,#000,transparent 78%);}
.nm-card{position:relative;width:min(30rem,100%);padding:var(--sp-6) var(--sp-5);text-align:center;
  background:linear-gradient(158deg,var(--tb-panel),var(--tb-abyss));box-shadow:var(--tb-lift);}
.nm-title{font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.3em;
  font-weight:800;color:var(--tb-muted);}
.nm-h{font-family:var(--tb-font);font-style:italic;text-transform:uppercase;font-size:var(--fs-xl);
  letter-spacing:0.05em;font-weight:900;margin:var(--sp-2) 0 var(--sp-5);}
.nm-input{text-align:center;font-size:var(--fs-md);letter-spacing:0.08em;}
.nm-count{font-family:var(--tb-font-mono);font-size:var(--fs-micro);letter-spacing:0.2em;
  color:var(--tb-muted);margin:var(--sp-2) 0 var(--sp-4);text-align:right;}
.nm-warn{font-size:var(--fs-xs);color:var(--tb-warn);margin:calc(-1 * var(--sp-3)) 0 var(--sp-3);min-height:1rem;}
.nm-btn{width:100%;}
.nm-legal{font-size:var(--fs-xs);color:var(--tb-muted);margin-top:var(--sp-4);line-height:1.7;}
.nm-legal button{background:none;border:0;padding:0;font:inherit;color:var(--tb-cyan);cursor:pointer;
  text-decoration:underline;text-underline-offset:0.2em;}
@media (hover:hover) and (pointer:fine){.nm-legal button:hover{color:var(--tb-text);}}
`;

export class NameScreen {
  private root: HTMLDivElement;
  /** Abre un documento legal (LEG-1). Lo enchufa main.ts con la LegalScreen. */
  onOpenLegal: ((doc: "privacy" | "terms") => void) | null = null;

  constructor() {
    injectThemeTokens();
    if (!document.getElementById("nm-style")) {
      const s = document.createElement("style");
      s.id = "nm-style";
      s.textContent = CSS;
      document.head.appendChild(s);
    }
    this.root = document.createElement("div");
    this.root.className = "nm-root";
    document.body.appendChild(this.root);
  }

  /** Muestra la pantalla; llama a `onSubmit(nombre)` al confirmar. */
  /** ¿Está tapando la pantalla? (ver `covered` en main.ts) */
  get isOpen(): boolean {
    return this.root.classList.contains("open");
  }

  open(prefill: string, subtitle: string, onSubmit: (name: string) => void): void {
    this.root.innerHTML = "";
    const card = document.createElement("div");
    card.className = "nm-card tb-cut tb-edge";
    card.innerHTML =
      `<div class="nm-title">DISCO FOOTBALL</div><div class="nm-h">${subtitle}</div>`;

    const input = document.createElement("input");
    input.className = "nm-input tb-input";
    input.type = "text";
    input.maxLength = MAX_NAME;
    input.placeholder = "Tu nombre";
    input.value = prefill;
    card.appendChild(input);

    const count = document.createElement("div");
    count.className = "nm-count";
    card.appendChild(count);

    // SEC-2: aviso en vivo de que el nombre no se va a usar tal cual. Es el
    // mismo filtro que aplica el servidor, así que lo que se avisa aquí es
    // exactamente lo que pasará — nada de sorpresas al entrar a la sala.
    const warn = document.createElement("div");
    warn.className = "nm-warn";
    card.appendChild(warn);

    const btn = document.createElement("button");
    btn.className = "nm-btn tb-btn tb-cut-sm primary";
    btn.textContent = "▶ CONTINUAR";
    card.appendChild(btn);

    const sync = (): void => {
      const typed = input.value;
      const verdict = checkName(typed);
      const v = verdict.name;
      count.textContent = `${Math.min(typed.trim().length, MAX_NAME)}/${MAX_NAME}`;
      // Vacío = aún no ha escrito nada: no hay nada que avisar, solo bloquear.
      const empty = typed.trim() === "";
      btn.disabled = empty;
      warn.textContent = empty ? ""
        : verdict.reason === "profanity" ? `Ese nombre se mostrará como "${v}".`
          : verdict.reason === "too-long" ? `Se recortará a "${v}".`
            : verdict.reason === "invalid-chars" ? `Se mostrará como "${v}".`
              : "";
    };
    input.oninput = sync;
    const submit = (): void => {
      const v = sanitizeName(input.value);
      if (!v) return;
      this.close();
      onSubmit(v);
    };
    btn.onclick = submit;
    input.onkeydown = (e) => {
      if (e.key !== "Enter") return;
      // Consumir el Enter: sin esto, el mismo evento burbujea a `window` y abre el
      // chat in-game que se monta justo al entrar a la partida (enterPlaying),
      // dejando al invitado "atrapado" escribiendo y sin poder moverse (TAREA B).
      e.preventDefault();
      e.stopPropagation();
      submit();
    };

    // LEG-1: los textos legales son accesibles desde la primera pantalla, antes
    // de que el jugador introduzca ningún dato.
    const legal = document.createElement("div");
    legal.className = "nm-legal";
    legal.appendChild(document.createTextNode("Al continuar aceptas los "));
    const terms = document.createElement("button");
    terms.type = "button";
    terms.textContent = "términos de uso";
    terms.onclick = () => this.onOpenLegal?.("terms");
    legal.appendChild(terms);
    legal.appendChild(document.createTextNode(" y la "));
    const privacy = document.createElement("button");
    privacy.type = "button";
    privacy.textContent = "política de privacidad";
    privacy.onclick = () => this.onOpenLegal?.("privacy");
    legal.appendChild(privacy);
    legal.appendChild(document.createTextNode("."));
    card.appendChild(legal);

    this.root.appendChild(card);
    this.root.classList.add("open");
    sync();
    setTimeout(() => { input.focus(); input.select(); }, 0);
  }

  close(): void {
    this.root.classList.remove("open");
    this.root.innerHTML = "";
  }
}
